import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import type {
  CreateStorageLocationPayload,
  StorageLocationNode
} from '../Types';
import { useFixedDropdownPosition } from '../Hooks/UseFixedDropdownPosition';
import { AddStorageLocationForm } from './AddStorageLocationForm';
import {
  formatLocationLabel,
  formatNumberedLocationLabel,
  formatStorageLocationPath,
  getCreatableChildLocationType,
  getActiveChildLocations,
  getRootStorageLocations,
  locationHasActiveChildren
} from '../Utils/StorageLocation.utils';

interface InventoryLocationSelectProps {
  value: string | null | undefined;
  locations: StorageLocationNode[];
  onChange: (locationId: string) => void;
  onCreateLocation?: (
    payload: CreateStorageLocationPayload
  ) => Promise<StorageLocationNode>;
  canCreateLocation?: boolean;
  creatingLocation?: boolean;
  hasError?: boolean;
  disabled?: boolean;
  compact?: boolean;
  className?: string;
}

const PLACEHOLDER = 'Select storage location';

const LocationNumber = ({ index }: { index: number }) => (
  <span
    className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-gray-100 text-[10px] font-semibold tabular-nums text-gray-500"
    aria-hidden
  >
    {index + 1}
  </span>
);

interface LocationRowButtonProps {
  index?: number;
  label: string;
  isSelected: boolean;
  onClick: () => void;
  trailing?: ReactNode;
  muted?: boolean;
}

const LocationRowButton = ({
  index,
  label,
  isSelected,
  onClick,
  trailing,
  muted = false
}: LocationRowButtonProps) => (
  <button
    type="button"
    role="option"
    aria-selected={isSelected}
    onClick={onClick}
    className={[
      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50',
      isSelected
        ? 'bg-[#006B82]/5 font-medium text-[#006B82]'
        : muted
          ? 'text-gray-400'
          : 'text-gray-800'
    ].join(' ')}
  >
    {index !== undefined && !isSelected ? (
      <LocationNumber index={index} />
    ) : (
      <span className="w-5 shrink-0" />
    )}
    <span className="min-w-0 flex-1 truncate">{label}</span>
    {trailing}
  </button>
);

export const InventoryLocationSelect = ({
  value,
  locations,
  onChange,
  onCreateLocation,
  canCreateLocation = false,
  creatingLocation = false,
  hasError = false,
  disabled = false,
  compact = false,
  className = ''
}: InventoryLocationSelectProps) => {
  const [open, setOpen] = useState(false);
  const [navStack, setNavStack] = useState<StorageLocationNode[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const dropdownStyle = useFixedDropdownPosition(open && !disabled, rootRef);

  const displayLabel = useMemo(
    () => formatStorageLocationPath(locations, value) ?? PLACEHOLDER,
    [locations, value]
  );

  const currentFolder =
    navStack.length > 0 ? navStack[navStack.length - 1] : null;
  const listItems = useMemo(
    () =>
      currentFolder
        ? getActiveChildLocations(currentFolder)
        : getRootStorageLocations(locations),
    [currentFolder, locations]
  );

  const currentFolderLabel = useMemo(() => {
    if (!currentFolder) return '';

    const roots = getRootStorageLocations(locations);
    const rootIndex = roots.findIndex(root => root.id === currentFolder.id);
    if (rootIndex >= 0) {
      return formatNumberedLocationLabel(currentFolder, rootIndex);
    }

    const parent =
      navStack.length > 1
        ? navStack[navStack.length - 2]
        : roots.find(r =>
            getActiveChildLocations(r).some(
              child => child.id === currentFolder.id
            )
          );
    const siblings = parent ? getActiveChildLocations(parent) : roots;
    const index = siblings.findIndex(item => item.id === currentFolder.id);
    return index >= 0
      ? formatNumberedLocationLabel(currentFolder, index)
      : formatLocationLabel(currentFolder);
  }, [currentFolder, locations, navStack]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        rootRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      )
        return;
      setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setNavStack([]);
    }
  }, [open]);

  const close = () => setOpen(false);

  const selectLocation = (locationId: string) => {
    onChange(locationId);
    close();
  };

  const handleOpen = () => {
    if (disabled) return;
    setNavStack([]);
    setOpen(prev => !prev);
  };

  const handleBack = () => {
    setNavStack(prev => prev.slice(0, -1));
  };

  const handleDrill = (node: StorageLocationNode) => {
    setNavStack(prev => [...prev, node]);
  };

  const handleCreatedLocation = (created: StorageLocationNode) => {
    onChange(created.id);
    if (getCreatableChildLocationType(created)) {
      setNavStack(prev => [...prev, created]);
      return;
    }
    close();
  };

  const showCreateForm = canCreateLocation && Boolean(onCreateLocation);
  const heightClass = compact ? 'h-9 text-sm' : 'h-10 text-sm';

  return (
    <div
      ref={rootRef}
      className={['relative min-w-0 w-full', className]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={handleOpen}
        className={[
          // Light surface regardless of theme — the inventory UI and this
          // control's own dropdown are always rendered on white.
          'flex w-full min-w-0 items-center gap-1 rounded-md border bg-white text-left text-gray-900',
          hasError ? 'border-red-400' : 'border-gray-200',
          compact ? 'px-2' : 'px-3',
          'hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#006B82]/30',
          heightClass,
          disabled ? 'cursor-not-allowed opacity-50' : '',
          !value ? 'text-gray-400' : ''
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label="Storage location"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="truncate">{displayLabel}</span>
        <ChevronDown
          className={[
            'ml-auto h-4 w-4 shrink-0 text-gray-400 transition-transform',
            open ? 'rotate-180' : ''
          ].join(' ')}
          aria-hidden
        />
      </button>

      {open &&
        !disabled &&
        createPortal(
          <div
            ref={panelRef}
            role="listbox"
            style={{ ...dropdownStyle, minWidth: 280 }}
            className="overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
          >
            {currentFolder && (
              <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-2 py-1.5">
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex shrink-0 items-center gap-0.5 rounded-md px-2 py-1 text-xs font-medium text-[#006B82] hover:bg-[#006B82]/5"
                  aria-label="Back to parent locations"
                >
                  <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
                  Back
                </button>
                <span className="min-w-0 truncate text-xs font-semibold text-gray-600">
                  {currentFolderLabel}
                </span>
              </div>
            )}

            <ul className="max-h-52 overflow-auto py-1">
              {!value && !currentFolder && (
                <li>
                  <LocationRowButton
                    label={PLACEHOLDER}
                    isSelected={!value}
                    onClick={() => selectLocation('')}
                    muted
                  />
                </li>
              )}

              {currentFolder && (
                <li>
                  <LocationRowButton
                    label={`Use ${formatLocationLabel(currentFolder)}`}
                    isSelected={value === currentFolder.id}
                    onClick={() => selectLocation(currentFolder.id)}
                  />
                </li>
              )}

              {listItems.map((item, index) => {
                const hasChildren = locationHasActiveChildren(item);
                const isSelected = value === item.id;
                const label = formatLocationLabel(item);

                if (hasChildren) {
                  return (
                    <li key={item.id}>
                      <LocationRowButton
                        index={index}
                        label={label}
                        isSelected={isSelected}
                        onClick={() => handleDrill(item)}
                        trailing={
                          <ChevronRight
                            className="h-4 w-4 shrink-0 text-gray-400"
                            aria-hidden
                          />
                        }
                      />
                    </li>
                  );
                }

                return (
                  <li key={item.id}>
                    <LocationRowButton
                      index={index}
                      label={label}
                      isSelected={isSelected}
                      onClick={() => selectLocation(item.id)}
                    />
                  </li>
                );
              })}

              {listItems.length === 0 && !showCreateForm && (
                <li className="px-3 py-2 text-sm text-gray-500">
                  No sub-locations
                </li>
              )}
            </ul>

            {showCreateForm && onCreateLocation && (
              <AddStorageLocationForm
                parent={currentFolder}
                submitting={creatingLocation}
                onCreate={onCreateLocation}
                onCreated={handleCreatedLocation}
              />
            )}
          </div>,
          document.body
        )}
    </div>
  );
};
